import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import type { MonacoEditor } from '../CodeEditor';
import { DescriptionLink, type DescriptionLinkProps } from '../DescriptionLink';
import { CodeInput, type CodeInputProps } from './CodeInput';
import { ErrorMessage, FormField } from './FormField';
import { getError } from './errors';
import { selectorsSchema } from './schema';

const fieldName = 'selectors';
const fieldLabel = selectorsSchema.describe().label;
const helpLink =
  'https://www.notion.so/demostack/Data-Rules-How-To-8798490f2d4a4b47a83df58750674213';

export interface SelectorsFieldProps
  extends Pick<CodeInputProps, 'height'>,
    Pick<DescriptionLinkProps, 'onLearnMoreClick'> {
  autoFocus?: boolean;
  onError?: (messages: string[]) => void;
  hideLabel?: boolean;
}

export const SelectorsField = ({
  onError,
  height = 300,
  autoFocus,
  hideLabel,
  onLearnMoreClick,
}: SelectorsFieldProps) => {
  const editorRef = useRef<MonacoEditor>();
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors, isLoading },
  } = useFormContext();
  const selectors = watch(fieldName);
  const isFormatted = useRef(false);

  const formatJson = () =>
    editorRef.current
      ?.getAction('editor.action.formatDocument')
      ?.run()
      .then(() => {
        editorRef.current?.revealLine(1);
        isFormatted.current = true;
      });

  useEffect(() => {
    if (!isLoading) {
      formatJson();
    }
  }, [isLoading]);

  return (
    <FormField
      name={fieldName}
      label={!hideLabel && fieldLabel}
      tooltipContent={
        <DescriptionLink
          label="Not sure how to use selectors?"
          linkLabel="Learn more"
          linkUrl={helpLink}
          onLearnMoreClick={onLearnMoreClick}
        />
      }
    >
      <input
        {...register(fieldName)}
        hidden
      />
      <CodeInput
        onMount={(editor) => {
          editorRef.current = editor;
          formatJson().then(() => {
            if (autoFocus) {
              editor.focus();
            }
          });
        }}
        onChange={(selectors) => {
          setValue(fieldName, selectors, {
            shouldDirty: isFormatted.current,
          });
          trigger(fieldName);
        }}
        onError={(errors) => {
          const errorMessages = errors.map(({ message }) => message);
          onError?.(errorMessages);
        }}
        value={selectors}
        language="json"
        height={height}
        state={errors[fieldName] ? 'error' : 'normal'}
      />
      <ErrorMessage
        errors={
          errors?.[fieldName]
            ? { [fieldName]: getError(errors[fieldName]) }
            : {}
        }
      />
    </FormField>
  );
};
